import { Button, Stack, styled, Divider, OutlinedInput, Autocomplete } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import AddToCollectionCard, {
  WishlistAddToCollectionCardProps as AddToCollectionCardProps,
} from "../../Cards/WishlistAddToCollectionCard";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import { assetService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbObjectCollectionInfos } from "../../../Core/Api/Types/db-object-collection-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { UserHasAcls } from "../../../Utils/helpers";
import { CollectionType } from "../../../Core/Api/Types/db-collection-type";
import { CollectionLocation } from "../../../Core/Api/Types/db-collection-location";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  message: string;
};

export type ModalAddToCollectionElementProps = {
  itemIds: number[];
  mode?: "edit" | "add";
  type?: "wishlist" | "collection";
  onClose?: (props: CloseClbProps) => void;
};

export type ModalAddToCollectionProps = CommonModalProps<ModalAddToCollectionElementProps>;

const ModalAddToCollection = React.forwardRef<any, ModalAddToCollectionProps>((props, ref) => {
  const collectionModal = useCollectionActions();
  const { mode = "add", closeModal, onClose } = props;
  const isEditMode = mode === "edit";
  const { t } = useTranslation();

  const modalTitle = isEditMode ? t("modal:title.moveToAnotherCollection") : t("modal:title.addToCollection");
  const [selected, SetSelected] = React.useState<number[]>([]);
  const [CollectionData, SetCollectionData] = React.useState<DbObjectCollectionInfos[]>([]);
  const [SearchPatern, SetSearchPatern] = React.useState<string>("");
  const enqueueSnackbar = useCustomSnackbar();

  const handleDoneClick = () => {
    if (!isEditMode) {
      selected.forEach((x) =>
        assetService.AddObjectIdsToCollection(x, props.itemIds).then((response) => {
          if (response.success)
            enqueueSnackbar(t("alert:success.itemAddedToCollection", { count: props.itemIds.length }), "success");
          onClose && onClose({ message: t("modal:message.addToCollectionClosed") });
          closeModal();
        })
      );
    }
  };

  const SearchChange = (event) => {
    const {
      target: { value },
    } = event;
    if (event.target.id === "collectionFilter") SetSearchPatern(value.toLocaleLowerCase());
  };

  const SetDataCollection = (data) => {
    SetCollectionData(data);
    // no need for now
    // SetCollectionData(
    //   data.filter(
    //     (collection) => !(!collection.targetGroupIds?.includes(groupId!) && collection.targetUserIds?.includes(userId!))
    //   )
    // );
  };

  React.useEffect(() => {
    if (!UserHasAcls([AclTypes.aclCollectionStorefrontAdmin]) && !UserHasAcls([AclTypes.aclSuperUser]))
      assetService.GetCollectionWishList().then((data) => {
        SetDataCollection(data.filter((x) => !x.isShared));
      });
    else
      assetService.ListCollection().then((data) => {
        SetDataCollection(data.filter((x) => !x.isShared || (x.isShared && x.type === CollectionType.Storefront)));
      });
  }, []);

  const collectionSliderData: AddToCollectionCardProps[] = React.useMemo(
    () =>
      CollectionData.map((collection) => {
        const covers =
          collection?.objectIds != undefined && collection?.objectIds?.length > 4
            ? collection.objectIds
                ?.slice(0, -4)
                .map<string>((objectId) => thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small))
            : collection.objectIds?.map<string>((objectId) =>
                thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small)
              ) || [];

        return {
          isCreationCard: false,
          id: collection.id,
          title: collection.name!,
          nbTitles: collection.objectInfos?.filter((x) => x.layer === ObjectLayer.Title).length || 0,
          nbTotaleItem: collection.objectIds?.length || 0,
          covers: [covers[0] || "", covers[1] || "", covers[2] || "", covers[3] || ""],
          selectMode: false,
          small: true,
          clickable: false,
          creationInfos: collection.creationInfos,
          location: collection.location || CollectionLocation.List,
        };
      }),
    [CollectionData]
  );

  /* const titleItemListData = React.useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        title: item.name,
        poster: thumbnailService.GetObjectPosterUrl(item.id, ThumbnailSize.small),
      })),
    [items]
  );*/

  const selectCollection = (wishlistAddToCollectionCardProps: AddToCollectionCardProps, isSelected: boolean) => {
    if (isSelected) {
      SetSelected(selected.filter((x) => x !== wishlistAddToCollectionCardProps.id));
    } else {
      SetSelected([...selected, wishlistAddToCollectionCardProps.id]);
    }
  };

  const lastFour = React.useMemo(() => {
    return collectionSliderData.slice(-4).map((addToCollectionCardProps) => {
      let isSelected = selected.includes(addToCollectionCardProps.id);
      return (
        <AddToCollectionCard
          key={addToCollectionCardProps.id}
          fixedWidth={52}
          direction="row"
          onClick={() => selectCollection(addToCollectionCardProps, isSelected)}
          isSelected={isSelected}
          {...addToCollectionCardProps}
        />
      );
    });
  }, [selected.length, collectionSliderData.length]);

  return (
    <Modal
      title={modalTitle}
      reference={ref}
      closeModal={closeModal}
      width={504}
      footer={
        isEditMode ? (
          <>
            <Button
              disabled={selected.length <= 0}
              color="primary"
              variant="contained"
              onClick={handleDoneClick}
              sx={{ width: "100%" }}
              startIcon={<MoveUpIcon />}
            >
              {t("button:moveToSelected")}
            </Button>
          </>
        ) : (
          <>
            <Button
              disabled={selected.length <= 0}
              color="primary"
              variant="contained"
              onClick={handleDoneClick}
              sx={{ width: "100%", mb: 1 }}
              startIcon={<MoveUpIcon />}
            >
              {t("button:addToCollection", { count: selected.length })}
            </Button>
            <Button
              onClick={() => collectionModal.CreateNewcollectionTypeWishList(props.itemIds, true)}
              sx={{ width: "100%" }}
              startIcon={<AddIcon />}
            >
              {t("button:createANewCollection")}
            </Button>
          </>
        )
      }
    >
      {/*titleItemListData.length > 0 && (
        <>
          <TitleItemList list={titleItemListData} />
          <Divider sx={{ mt: 1, mb: 2 }} />
        </>
      )*/}
      <StyledAutocomplete
        options={collectionSliderData}
        sx={{ mb: 2 }}
        popupIcon={
          <>
            <Divider orientation="vertical" sx={{ height: 20, mr: 1 }} />
            <SearchIcon />
          </>
        }
        onChange={(event, newValue) => {
          if (!newValue) return;
          let isSelected = selected.includes(newValue.id);
          selectCollection(newValue, isSelected);
        }}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, value) => {
          return selected.includes(option.id);
        }}
        renderTags={() => <></>}
        renderInput={(params) => (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            id="collectionFilter"
            label={t("common:standard")}
            size="medium"
            placeholder={t("field:placeholder.searchCollections")}
            value={SearchPatern}
            onChange={SearchChange}
            sx={{ width: "100%" }}
          />
        )}
      ></StyledAutocomplete>
      <StyledStackContainer spacing={1}>{lastFour}</StyledStackContainer>
      <StyledStackContainer spacing={1}>
        {collectionSliderData
          .slice(0, collectionSliderData.length - 4)
          .filter((x) => selected.includes(x.id))
          .map((addToCollectionCardProps) => {
            let isSelected = selected.includes(addToCollectionCardProps.id);
            return (
              <AddToCollectionCard
                key={addToCollectionCardProps.id}
                fixedWidth={52}
                direction="row"
                onClick={() => selectCollection(addToCollectionCardProps, isSelected)}
                isSelected={isSelected}
                {...addToCollectionCardProps}
              />
            );
          })}
      </StyledStackContainer>
    </Modal>
  );
});

export default ModalAddToCollection;

const StyledStackContainer = styled(Stack)(({ theme }) => ({
  maxHeight: 390,
  overflow: "hidden",
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ".MuiIconButton-root": { transform: "none !important", svg: { color: theme.palette.primary.main } },
})) as typeof Autocomplete;
